/**
 * Sign-in — one card, two methods on a remembered segment (design P1).
 * Email/username + password and mobile OTP are both live against Auth for
 * every clinic (no per-clinic login policy exists — ZOY-34 was canceled).
 * A correct TEMPORARY password answers 403 PASSWORD_CHANGE_REQUIRED
 * (ZOY-283) and routes to Set New Password, which completes the change and
 * opens the session in one step — distinct from Forgot Password, where the
 * password is unknown. Every failure keeps every typed value. Throttling
 * renders the SERVER's wait and nothing else: the resend control counts down
 * `resendCooldownSeconds` / `retryAfterSeconds` exactly as Auth supplies them
 * (ZOY-72 tells clients to drive it from those), and no wait is ever guessed.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { takeIntent } from '@/core/auth/returnTo';
import { peekEntry } from '@/features/entry/entryIntent';
import { useIntl } from 'react-intl';
import s from './access.module.css';
import {
  Button,
  Machine,
  Notice,
  OtpInput,
  PasswordField,
  PasswordRulesPanel,
  Segmented,
  TextField,
} from '@/ui/kit';
import { useSession } from '@/core/auth/SessionProvider';
import { AuthCode, otpRequest } from '@/core/auth/api';
import { isE164, normalizeIranMobile } from '@/core/auth/phone';
import { DevOtpNotice, devOtpFromAck } from './DevOtpNotice';
import {
  blocksResend,
  cooldownEndsAt,
  formatWait,
  OTP_DELIVERY_FAILED,
  otpDeliveryFailedFrom,
  otpThrottleCopy,
  otpThrottleFrom,
  remainingSeconds,
  type ResendBlockReason,
} from './otpThrottle';
import { useServerWait } from './useServerWait';
import { ApiError, NetworkError } from '@/core/api/errors';
import { passwordRules, rulesSatisfied } from './passwordRules';
import { ruleRows } from './passwordRuleRows';
import { useLocale, type Locale } from '@/core/i18n/LocaleProvider';

type Method = 'mobile' | 'email';
type Phase =
  | { kind: 'form' }
  | { kind: 'otp-code' }
  | { kind: 'success'; name: string }
  | { kind: 'inactive' }
  | { kind: 'not-ready' }
  /** ZOY-283: the temporary password was correct; it must be replaced to sign in. */
  | { kind: 'set-password' };

const METHOD_KEY = 'zc.signinMethod';

function readMethod(): Method {
  try {
    return localStorage.getItem(METHOD_KEY) === 'email' ? 'email' : 'mobile';
  } catch {
    return 'mobile';
  }
}

export function SignInPage() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const navigate = useNavigate();
  const { signInPassword, completeFirstLogin, signInOtp } = useSession();
  const { locale, setLocale } = useLocale();

  const [method, setMethodState] = useState<Method>(readMethod);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  const [busy, setBusy] = useState(false);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  /** ZOY-305: the E.164 the code was actually requested for — verify/resend use it. */
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  const [newPwError, setNewPwError] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null); // TEMP dev aid (DevOtpNotice)

  const [banner, setBanner] = useState<React.ReactNode>(null);
  const [codeError, setCodeError] = useState(false);
  /**
   * The resend gate. `cooldownUntil` is always derived from a number Auth
   * sent — the ack's resendCooldownSeconds, or a 429's retryAfterSeconds.
   * `throttle` names WHY, so a resend gap never reads as a wrong-code lockout.
   */
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [throttle, setThrottle] = useState<ResendBlockReason | null>(null);
  /** ZOY-768: after a 503 on the phone step, "Send" waits out Auth's retryAfterSeconds. */
  const sendWait = useServerWait();
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // One ticker, alive only while a wait is actually running.
  useEffect(() => {
    if (cooldownUntil === null || cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const waitLeft = remainingSeconds(cooldownUntil, now);
  const resendBlocked = waitLeft > 0;

  const setMethod = (m: Method) => {
    setMethodState(m);
    setBanner(null);
    try {
      localStorage.setItem(METHOD_KEY, m);
    } catch {
      /* ignore */
    }
  };

  // ZOY-614: a sign-in that started at a clinic's entry URL goes back there,
  // where the membership check and the preselection happen. Otherwise
  // ZOY-610's rule: the same person's remembered deep link, else Today.
  const entry = peekEntry();
  const finish = useCallback(
    (uid: string) => {
      const pending = peekEntry();
      const dest = pending ? `/c/${pending.slug}` : (takeIntent(uid) ?? '/home');
      setTimeout(() => void navigate({ to: dest }), 900);
    },
    [navigate],
  );

  const handleAuthError = (err: unknown): boolean => {
    if (err instanceof NetworkError) {
      setBanner(
        <Notice kind="warn" title={t('signin.netTitle')} role="alert">
          {t('signin.netBody')}
        </Notice>,
      );
      return true;
    }
    if (err instanceof ApiError) {
      const account = err.accountState;
      if (err.status === 403 && err.authCode === AuthCode.PasswordChangeRequired) {
        setNewPw('');
        setNewPwConfirm('');
        setNewPwError(null);
        setPhase({ kind: 'set-password' });
        return true;
      }
      if (err.status === 403 && err.authCode === AuthCode.WrongLoginSurface) {
        setBanner(
          <Notice kind="err" title={t('signin.wrongSurfaceTitle')} role="alert">
            {t('signin.wrongSurfaceBody')}
          </Notice>,
        );
        return true;
      }
      if (account === 'ACCOUNT_NOT_READY') {
        setPhase({ kind: 'not-ready' });
        return true;
      }
      if (account !== undefined) {
        setPhase({ kind: 'inactive' });
        return true;
      }
      const th = otpThrottleFrom(err);
      if (th !== null) {
        // Auth distinguishes three causes; collapsing them would tell the user
        // "too many attempts" for what was a 60-second resend gap.
        const { titleKey, bodyKey } = otpThrottleCopy(th.code);
        setThrottle(th.code);
        if (blocksResend(th.code))
          setCooldownUntil(cooldownEndsAt(th.retryAfterSeconds, Date.now()));
        setNow(Date.now());
        setBanner(
          <Notice kind="warn" title={t(titleKey)} role="alert">
            {t(bodyKey)}
            {th.retryAfterSeconds !== undefined && (
              <div style={{ marginBlockStart: 6 }}>
                {t('otp.throttledBody', { wait: formatWait(intl, th.retryAfterSeconds) })}
              </div>
            )}
            <div style={{ marginBlockStart: 6, fontSize: 12 }}>{t('otp.serverWaitNote')}</div>
          </Notice>,
        );
        return true;
      }
    }
    return false;
  };

  const notSentNotice = (retryAfterSeconds: number | undefined) => (
    <Notice kind="err" title={t('otp.notSentTitle')} role="alert">
      {t('otp.notSentBody')}
      {retryAfterSeconds !== undefined && retryAfterSeconds > 0 && (
        <div style={{ marginBlockStart: 6 }}>
          {t('otp.resendWait', { wait: formatWait(intl, retryAfterSeconds) })}
        </div>
      )}
    </Notice>
  );

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);
    setBusy(true);
    try {
      const me = await signInPassword(identifier.trim(), password);
      setPhase({ kind: 'success', name: identifier.trim() });
      finish(me.uid);
    } catch (err) {
      if (!handleAuthError(err)) {
        setBanner(
          <Notice kind="err" title={t('signin.wrongCreds')} role="alert">
            {t('signin.wrongCredsBody')}
          </Notice>,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  /** ZOY-283 completion: identifier + the temporary password + the new one → session. */
  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewPwError(null);
    setBanner(null);
    setBusy(true);
    try {
      const me = await completeFirstLogin(identifier.trim(), password, newPw);
      setPhase({ kind: 'success', name: identifier.trim() });
      finish(me.uid);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.authCode;
        if (err.status === 400 && code === AuthCode.PasswordUnchanged) {
          setNewPwError(t('signin.setNewUnchanged') as string);
          return;
        }
        if (err.status === 400) {
          // ZOY-632: named in the reader's language; the panel shows which rule fails.
          setNewPwError(t('signin.setNewRejected') as string);
          return;
        }
        if (err.status === 409 && code === AuthCode.PasswordChangeNotRequired) {
          setPhase({ kind: 'form' });
          setBanner(
            <Notice kind="info" role="alert">
              {t('signin.setNewNotRequired')}
            </Notice>,
          );
          return;
        }
        if (err.status === 401) {
          // The temporary password stopped matching (e.g. reissued meanwhile).
          setPhase({ kind: 'form' });
          setBanner(
            <Notice kind="err" title={t('signin.wrongCreds')} role="alert">
              {t('signin.wrongCredsBody')}
            </Notice>,
          );
          return;
        }
      }
      if (!handleAuthError(err)) setNewPwError(t('common.failedKept') as string);
    } finally {
      setBusy(false);
    }
  };

  const submitPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);
    setPhoneError(null);
    // ZOY-305: Auth requires E.164; the field is designed for national entry
    // (+98 prefix, tel-national), so the client normalizes before sending. A
    // value that cannot be a phone number is refused here with a human
    // message instead of being posted raw.
    const e164 = normalizeIranMobile(phone);
    if (!isE164(e164)) {
      setPhoneError(t('signin.phoneInvalid') as string);
      return;
    }
    setBusy(true);
    try {
      const ack = await otpRequest(e164);
      setSentTo(e164);
      setDevOtp(devOtpFromAck(ack)); // TEMP: shown only when Auth echoes devCode
      setCode('');
      setCodeError(false);
      // Auth's cooldown starts with THIS send, so the resend control opens
      // only when the server would actually accept another request.
      setThrottle(null);
      setCooldownUntil(cooldownEndsAt(ack.resendCooldownSeconds, Date.now()));
      setNow(Date.now());
      setPhase({ kind: 'otp-code' });
    } catch (err) {
      // ZOY-768: no code was sent — stay on the phone step with the number,
      // say so, and wait out the server's cooldown before offering Send again.
      const notSent = otpDeliveryFailedFrom(err);
      if (notSent) {
        sendWait.start(notSent.retryAfterSeconds);
        setBanner(notSentNotice(notSent.retryAfterSeconds));
        return;
      }
      if (!handleAuthError(err)) {
        // ZOY-305: no server response may leave the form inert.
        if (err instanceof ApiError && err.status === 400) {
          setPhoneError(t('signin.phoneInvalid') as string);
        } else {
          setBanner(
            <Notice kind="err" role="alert">
              {t('signin.otpReqFailed')}
            </Notice>,
          );
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    setBusy(true);
    setCodeError(false);
    setBanner(null);
    try {
      const me = await signInOtp(sentTo, value);
      setDevOtp(null);
      setPhase({ kind: 'success', name: sentTo });
      finish(me.uid);
    } catch (err) {
      if (!handleAuthError(err)) setCodeError(true);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    // The server would refuse anyway; never fire a request the contract says
    // is not allowed yet.
    if (resendBlocked || resending) return;
    setBanner(null);
    setResending(true);
    try {
      const ack = await otpRequest(sentTo);
      setDevOtp(devOtpFromAck(ack)); // TEMP: newest code replaces the old one
      setThrottle(null);
      setCooldownUntil(cooldownEndsAt(ack.resendCooldownSeconds, Date.now()));
      setNow(Date.now());
      setBanner(
        <Notice kind="ok" title={t('otp.resent')} role="status">
          {t('otp.resentBody')}
        </Notice>,
      );
      // A new code invalidates the old one, so the field starts clean —
      // but ONLY on success. A refused resend keeps what was typed.
      setCode('');
      setCodeError(false);
    } catch (err) {
      // ZOY-768: the new code was not sent. The old one may still work, the
      // typed digits stay, and the resend control waits out the server's
      // cooldown — worded as "not sent", never as a code on its way.
      const notSent = otpDeliveryFailedFrom(err);
      if (notSent) {
        setThrottle(OTP_DELIVERY_FAILED);
        setCooldownUntil(cooldownEndsAt(notSent.retryAfterSeconds, Date.now()));
        setNow(Date.now());
        setBanner(notSentNotice(notSent.retryAfterSeconds));
        return;
      }
      if (!handleAuthError(err)) {
        setBanner(
          <Notice kind="err" title={t('otp.resendFailedTitle')} role="alert">
            {t('otp.resendFailedBody')}
          </Notice>,
        );
      }
    } finally {
      setResending(false);
    }
  };

  const langSeg = (
    <div className={s.segRow}>
      <Segmented<Locale>
        label={t('account.language')}
        options={[
          { value: 'fa', label: 'فارسی' },
          { value: 'en', label: 'English' },
        ]}
        value={locale}
        onChange={setLocale}
      />
    </div>
  );

  return (
    <div className={s.page}>
      <div>
        <div className={s.card}>
          <div className={s.stack}>
            <div className={s.wordmark}>
              <span className={s.wordmarkLatin}>{t('app.product')}</span>
              <span className={s.wordmarkFa}>{t('app.productFa')}</span>
              <span className={s.wordmarkTag}>{t('app.clinicTag')}</span>
            </div>

            {entry && phase.kind === 'form' && (
              <Notice kind="info">
                {t('entry.brandedSignIn', { clinic: entry.displayName ?? entry.slug })}
              </Notice>
            )}

            {phase.kind === 'form' && (
              <>
                <h1 className={s.title}>{t('signin.title')}</h1>
                <p className={s.sub}>
                  {method === 'email' ? t('signin.subEmail') : t('signin.subMobile')}
                </p>
                <div className={s.segRow}>
                  <Segmented<Method>
                    label={t('signin.title')}
                    options={[
                      { value: 'mobile', label: t('signin.methodMobile') },
                      { value: 'email', label: t('signin.methodEmail') },
                    ]}
                    value={method}
                    onChange={setMethod}
                  />
                </div>
                {banner}
                {method === 'email' ? (
                  <form className={s.stack} onSubmit={submitPassword}>
                    <TextField
                      label={t('signin.identifierLabel')}
                      value={identifier}
                      onChange={setIdentifier}
                      help={t('signin.identifierHelp')}
                      machine
                      autoComplete="username"
                      name="identifier"
                    />
                    <PasswordField
                      label={t('signin.passwordLabel')}
                      value={password}
                      onChange={setPassword}
                      help={t('signin.passwordHelp')}
                      autoComplete="current-password"
                      showWord="show"
                      hideWord="hide"
                    />
                    <Button
                      type="submit"
                      variant="brand"
                      disabled={busy || identifier === '' || password === ''}
                      busy={busy}
                    >
                      {busy ? t('signin.sending') : t('signin.cta')}
                    </Button>
                    <div className={s.centerRow}>
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => void navigate({ to: '/forgot' })}
                      >
                        {t('signin.forgot')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form className={s.stack} onSubmit={submitPhone}>
                    <TextField
                      label={t('signin.phoneLabel')}
                      value={phone}
                      onChange={(v) => {
                        setPhone(v);
                        setPhoneError(null);
                      }}
                      help={t('signin.phoneHelp')}
                      error={phoneError ?? undefined}
                      machine
                      type="tel"
                      prefix="+98"
                      inputMode="tel"
                      autoComplete="tel-national"
                      name="phone"
                    />
                    <Button
                      type="submit"
                      variant="brand"
                      disabled={busy || phone.trim() === '' || sendWait.left > 0}
                      busy={busy}
                    >
                      {sendWait.left > 0
                        ? t('otp.resendWait', { wait: formatWait(intl, sendWait.left) })
                        : t('signin.sendCode')}
                    </Button>
                    <div className={s.ctaNote}>{t('signin.otpNote')}</div>
                  </form>
                )}
              </>
            )}

            {phase.kind === 'otp-code' && (
              <>
                <h1 className={s.title}>{t('otp.codeTitle')}</h1>
                <p className={s.sub}>
                  {t('otp.codeSubA')}
                  <Machine>{sentTo}</Machine>
                  {t('otp.codeSubB')}
                </p>
                {banner}
                <DevOtpNotice code={devOtp} />
                <OtpInput
                  value={code}
                  onChange={setCode}
                  onComplete={(v) => void submitCode(v)}
                  error={codeError}
                  label={t('otp.codeLabel')}
                />
                <div
                  className={s.ctaNote}
                  role={codeError ? 'alert' : undefined}
                  style={codeError ? { color: 'var(--zc-err-ink)' } : undefined}
                >
                  {codeError ? t('otp.invalid') : t('otp.codeHelp')}
                </div>
                <Button
                  type="button"
                  variant="brand"
                  disabled={busy || code.length < 6}
                  busy={busy}
                  onClick={() => void submitCode(code)}
                >
                  {t('otp.verify')}
                </Button>
                <div className={s.centerRow}>
                  <Button
                    type="button"
                    variant="link"
                    disabled={resendBlocked || resending}
                    busy={resending}
                    onClick={() => void resend()}
                  >
                    {resendBlocked
                      ? t('otp.resendWait', { wait: formatWait(intl, waitLeft) })
                      : t('otp.resend')}
                  </Button>
                </div>
                {/* The reason sits WITH the control that is unavailable — the
                    banner at the top of the card is easy to miss from here. */}
                <div
                  className={s.ctaNote}
                  role={resendBlocked ? 'status' : undefined}
                  aria-live={resendBlocked ? 'polite' : undefined}
                >
                  {resendBlocked ? t(otpThrottleCopy(throttle).bodyKey) : t('otp.serverWaitNote')}
                </div>
                <div className={s.centerRow}>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      setDevOtp(null);
                      setBanner(null);
                      setPhase({ kind: 'form' });
                    }}
                  >
                    {t('signin.backToSignin')}
                  </Button>
                </div>
              </>
            )}

            {phase.kind === 'set-password' && (
              <>
                <h1 className={s.title}>{t('signin.setNewTitle')}</h1>
                <p className={s.sub}>{t('signin.setNewSub')}</p>
                <p className={s.sub}>
                  <Machine>{identifier.trim()}</Machine>
                </p>
                {banner}
                <form className={s.stack} onSubmit={submitNewPassword}>
                  <div>
                    <PasswordField
                      label={t('forgot.newPwLabel')}
                      value={newPw}
                      onChange={(v) => {
                        setNewPw(v);
                        setNewPwError(null);
                      }}
                      help={t('forgot.newPwHelp')}
                      error={newPwError ?? undefined}
                      autoComplete="new-password"
                      showWord="show"
                      hideWord="hide"
                    />
                    <PasswordRulesPanel
                      title={t('forgot.rulesTitle')}
                      rules={ruleRows(intl, passwordRules(newPw, password), 'temporary')}
                      stateWords={{
                        pending: t('pw.rulePending') as string,
                        met: t('pw.ruleMet') as string,
                        unmet: t('pw.ruleUnmet') as string,
                      }}
                      note={t('forgot.rulesNote')}
                    />
                  </div>
                  <PasswordField
                    label={t('forgot.confirmLabel')}
                    value={newPwConfirm}
                    onChange={setNewPwConfirm}
                    help={t('forgot.confirmHelp')}
                    error={
                      newPwConfirm !== '' && newPwConfirm !== newPw
                        ? t('forgot.mismatch')
                        : undefined
                    }
                    ok={newPw !== '' && newPwConfirm === newPw ? t('forgot.matchOk') : undefined}
                    autoComplete="new-password"
                    showWord="show"
                    hideWord="hide"
                  />
                  <Button
                    type="submit"
                    variant="brand"
                    disabled={
                      busy ||
                      newPw === '' ||
                      newPwConfirm !== newPw ||
                      !rulesSatisfied(passwordRules(newPw, password))
                    }
                    busy={busy}
                  >
                    {busy ? t('signin.sending') : t('signin.setNewCta')}
                  </Button>
                </form>
                <div className={s.ctaNote}>{t('signin.setNewNote')}</div>
                <div className={s.centerRow}>
                  <Button type="button" variant="link" onClick={() => setPhase({ kind: 'form' })}>
                    {t('signin.backToSignin')}
                  </Button>
                </div>
              </>
            )}

            {phase.kind === 'success' && (
              <div className={s.success} role="status">
                <span className={s.successTick} aria-hidden>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className={s.successTitle}>
                  {t('signin.successTitle', { name: phase.name })}
                </div>
                <div className={s.sub}>{t('signin.successGoing')}</div>
              </div>
            )}

            {phase.kind === 'inactive' && (
              <>
                <Notice kind="err" title={t('signin.inactiveTitle')} role="alert">
                  {t('signin.inactiveBody')}
                </Notice>
                <div className={s.centerRow}>
                  <Button type="button" variant="link" onClick={() => setPhase({ kind: 'form' })}>
                    {t('signin.backToSignin')}
                  </Button>
                </div>
              </>
            )}

            {phase.kind === 'not-ready' && (
              <>
                <Notice kind="warn" title={t('signin.notReadyTitle')} role="alert">
                  {t('signin.notReadyBody')}
                </Notice>
                <div className={s.centerRow}>
                  <Button type="button" variant="link" onClick={() => setPhase({ kind: 'form' })}>
                    {t('signin.backToSignin')}
                  </Button>
                </div>
              </>
            )}

            {langSeg}
          </div>
        </div>
        {phase.kind === 'form' && <div className={s.underCard}>{t('signin.underAuth')}</div>}
      </div>
    </div>
  );
}
